import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Label } from "@/components/Base/Label";
import { Input } from "@/components/Base/Input";
import { Textarea } from "@/components/Base/Textarea";
import { getTextColor } from "@/lib/utils";
import { Icon } from "@/components/Common/Icon";

export interface EditColorDialogProps {
  color: string; // The color of the clicked item
  existingTitle?: string; // Existing card title (if any)
  existingDescription?: string; // Existing description (if any)
  onSave?: (title: string, description: string) => void; // Save handler
  onCancel?: () => void; // Cancel handler
}

const EditColorDialog = ({
  color,
  existingTitle = "",
  existingDescription = "",
  onSave,
  onCancel,
}: EditColorDialogProps) => {
  const [title, setTitle] = useState(existingTitle);
  const [description, setDescription] = useState(existingDescription);
  const [open, setOpen] = useState(false);

  const handleSave = () => {
    if (onSave) {
      onSave(title, description);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setTitle(existingTitle);
    setDescription(existingDescription);
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-2"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Icon name={"editSquare"} className={"text-primary-foreground"} />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[270px] p-0">
        <div
          className="flex h-[60px] w-full items-center justify-center rounded-sm"
          style={{
            backgroundColor: color,
            color: getTextColor(color),
          }}
        >
          <DialogTitle>{title ? title : "Edit Color"}</DialogTitle>
        </div>

        <div className="px-5 py-2">
          <Label htmlFor={"colorInput"}>Card Color Label</Label>
          <Input
            id={"colorInput"}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Card color label"
          />
        </div>

        <div className="px-5 py-2">
          <Label htmlFor={"colorDescription"}>Description</Label>.
          <Textarea
            id={"colorDescription"}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Color description"
            className={"h-36"}
          />
        </div>

        <div className="flex flex-col items-center space-y-3 px-5 py-4">
          <Button
            variant="default"
            className="h-[40px] w-full rounded-md bg-[#2D67B2] text-lg font-bold text-white hover:opacity-80"
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            variant="ghost"
            className="w-full text-sm text-[#999999]"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

EditColorDialog.displayName = "EditColorDialog";

export { EditColorDialog };
